"use client";

import { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Mail, MapPin, Phone, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
const serviceTypes = [{
  value: "Installation",
  label: "Install"
}, {
  value: "Repair",
  label: "Repair"
}, {
  value: "Maintenance",
  label: "Service"
}, {
  value: "Estimate",
  label: "Quote"
}, {
  value: "Emergency Repair",
  label: "Urgent"
}];
const urgencyOptions = [{
  value: "same_day",
  label: "Today"
}, {
  value: "within_3_days",
  label: "3 Days"
}, {
  value: "flexible",
  label: "Flexible"
}];
function TickEyebrow({
  label
}: {
  label: string;
}) {
  return <div className="flex items-center gap-3">
      <span aria-hidden="true" className="inline-block h-3 w-[1.5px]" style={{
      backgroundColor: "var(--primary)"
    }} />
      <span className="font-sans text-xs font-semibold uppercase" style={{
      color: "var(--primary)",
      letterSpacing: "0.22em"
    }}>
        {label}
      </span>
      <span aria-hidden="true" className="inline-block h-3 w-[1.5px]" style={{
      backgroundColor: "var(--primary)"
    }} />
    </div>;
}
function ServiceRequestForm() {
  const [formDef, setFormDef] = useState<any>(null);
  const [fetchTime, setFetchTime] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [hpValue, setHpValue] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    service_type: "",
    urgency: "flexible",
    message: ""
  });
  const [ackChecked, setAckChecked] = useState(false);
  useEffect(() => {
    const client = getWvcClient();
    if (!client?.pluginRequest) return;
    client.pluginRequest("wvc-forms/v1/forms/homeowner_service_request").then((def: any) => {
      setFormDef(def);
      setFetchTime(Date.now());
    }).catch((err: any) => {
      if (err?.status === 404) {
        client.pluginRequest("wvc-forms/v1/forms").then((listRes: any) => {
          const items = listRes?.items || [];
          const match = items.find((f: any) => f.key === "homeowner_service_request") || (items.length === 1 ? items[0] : null) || items.find((f: any) => /service|booking|request/i.test(f.name));
          if (match?.key) {
            return client.pluginRequest(`wvc-forms/v1/forms/${match.key}`);
          }
        }).then((fallbackDef: any) => {
          if (fallbackDef) {
            setFormDef(fallbackDef);
            setFetchTime(Date.now());
          }
        }).catch(() => {});
      }
    });
  }, []);
  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const next = {
          ...prev
        };
        delete next[field];
        return next;
      });
    }
  };
  const validate = () => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim() || formData.name.trim().length < 2) {
      errors.name = "Please enter your full name.";
    }
    if (!formData.phone.trim()) {
      errors.phone = "Please enter your phone number.";
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = "Please enter a valid email address.";
    }
    if (!formData.address.trim()) {
      errors.address = "Please enter the service address.";
    }
    if (!formData.service_type) {
      errors.service_type = "Please select a service type.";
    }
    if (!formData.message.trim() || formData.message.trim().length < 10) {
      errors.message = "Please describe the problem.";
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate()) {
      return;
    }
    const client = getWvcClient();
    if (!client?.pluginRequest || !formDef) {
      setSubmitError("Error sending request. Please call us.");
      return;
    }
    setIsSubmitting(true);
    try {
      const minSec = formDef.spam?.min_seconds ?? 3;
      const elapsed = (Date.now() - fetchTime) / 1000;
      if (elapsed < minSec) {
        await new Promise(r => setTimeout(r, (minSec - elapsed) * 1000));
      }
      const values: Record<string, any> = {};
      const fields: any[] = formDef.fields || [];
      const setVal = (key: string, val: any) => {
        const matched = fields.find((f: any) => f.key === key);
        if (matched?.id) {
          values[matched.id] = val;
        }
      };
      setVal("full_name", formData.name.trim());
      setVal("email", formData.email.trim());
      setVal("phone", formData.phone.trim());
      setVal("service_type", formData.service_type);
      setVal("service_address", formData.address.trim());
      setVal("message", formData.message.trim());
      setVal("appliance_type", "");
      setVal("source_page", "HVAC Services");
      setVal("source_city", "");
      setVal("source_service", "HVAC Services");
      if (formData.urgency) {
        setVal("urgency", formData.urgency);
      }
      const formKey = formDef.key || "homeowner_service_request";
      const payload: any = {
        values,
        token: formDef.spam?.token,
        meta: {
          source_url: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : ""
        }
      };
      if (formDef.spam?.honeypot?.field) {
        payload.hp = {
          [formDef.spam.honeypot.field]: hpValue
        };
      }
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formKey}/submit`, {
        method: "POST",
        data: payload
      });
      if (res?.success) {
        if (res.confirmation?.type === "redirect" && res.confirmation?.url) {
          window.location.assign(res.confirmation.url);
        } else {
          setIsSubmitted(true);
        }
      } else if (res?.code === "frms_validation_failed") {
        setSubmitError(res?.message || "Please check the highlighted fields.");
        if (res.errors) {
          const newErrors: Record<string, string> = {};
          fields.forEach((f: any) => {
            if (res.errors[f.id]) {
              newErrors[f.key === "service_address" ? "address" : f.key === "full_name" ? "name" : f.key] = res.errors[f.id].message;
            }
          });
          setFieldErrors(newErrors);
        }
      } else if (res?.code === "frms_invalid_token") {
        setSubmitError("Session expired. Please reload and try again.");
      } else {
        setSubmitError(res?.message || "Error sending request. Please call us.");
      }
    } catch {
      setSubmitError("Error sending request. Please call us.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const resetForm = () => {
    setFormData({
      name: "",
      phone: "",
      email: "",
      address: "",
      service_type: "",
      urgency: "flexible",
      message: ""
    });
    setAckChecked(false);
    setFieldErrors({});
    setSubmitError(null);
    setIsSubmitted(false);
  };
  if (isSubmitted && !submitError) {
    return <div className="rounded-2xl p-10 text-center" style={{
      backgroundColor: "var(--card)",
      border: "1px solid var(--border)"
    }}>
        <h3 className="mb-3 font-mono text-2xl font-bold uppercase" style={{
        color: "var(--foreground)"
      }}>
          {"Request Sent"}
        </h3>
        <p className="mb-6 font-sans text-base" style={{
        color: "var(--muted-foreground)"
      }}>
          {"Request received. A pro will contact you shortly."}
        </p>
        <Button onClick={resetForm} className="h-14 rounded-lg px-8 font-sans text-sm font-semibold uppercase" style={{
        backgroundColor: "var(--primary)",
        color: "var(--primary-foreground)",
        letterSpacing: "0.06em"
      }}>
          {"New Request"}
        </Button>
      </div>;
  }
  const serviceTypeField = formDef?.fields?.find((f: any) => f.key === "service_type");
  const availableServiceTypes = serviceTypeField?.options?.length ? serviceTypeField.options : serviceTypes;
  return <div className="rounded-2xl p-8 md:p-12" style={{
    backgroundColor: "var(--card)",
    border: "1px solid var(--border)"
  }}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {formDef?.spam?.honeypot?.field && <input type="text" name={formDef.spam.honeypot.field} value={hpValue} onChange={e => setHpValue(e.target.value)} style={{
        position: "absolute",
        left: "-9999px"
      }} tabIndex={-1} autoComplete="off" aria-hidden="true" />}

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="grid gap-2">
            <label className="font-sans text-xs font-semibold uppercase" style={{
            color: "var(--muted-foreground)",
            letterSpacing: "0.14em"
          }}>
              {"Full Name"}
            </label>
            <Input value={formData.name} onChange={e => handleChange("name", e.target.value)} placeholder="Jane Smith" className="h-14 rounded-md px-5" style={{
            backgroundColor: "var(--background)",
            borderColor: "var(--border)",
            color: "var(--foreground)"
          }} />
            {fieldErrors.name && <p className="text-destructive text-sm font-sans">{fieldErrors.name}</p>}
          </div>

          <div className="grid gap-2">
            <label className="font-sans text-xs font-semibold uppercase" style={{
            color: "var(--muted-foreground)",
            letterSpacing: "0.14em"
          }}>
              {"Phone"}
            </label>
            <Input type="tel" value={formData.phone} onChange={e => handleChange("phone", e.target.value)} placeholder="(555) 000-0000" className="h-14 rounded-md px-5" style={{
            backgroundColor: "var(--background)",
            borderColor: "var(--border)",
            color: "var(--foreground)"
          }} />
            {fieldErrors.phone && <p className="text-destructive text-sm font-sans">{fieldErrors.phone}</p>}
          </div>
        </div>

        <div className="grid gap-2">
          <label className="font-sans text-xs font-semibold uppercase" style={{
          color: "var(--muted-foreground)",
          letterSpacing: "0.14em"
        }}>
            {"Email"}
          </label>
          <Input type="email" value={formData.email} onChange={e => handleChange("email", e.target.value)} placeholder="jane@email.com" className="h-14 rounded-md px-5" style={{
          backgroundColor: "var(--background)",
          borderColor: "var(--border)",
          color: "var(--foreground)"
        }} />
          {fieldErrors.email && <p className="text-destructive text-sm font-sans">{fieldErrors.email}</p>}
        </div>

        <div className="grid gap-2">
          <label className="font-sans text-xs font-semibold uppercase" style={{
          color: "var(--muted-foreground)",
          letterSpacing: "0.14em"
        }}>
            {"Service Address"}
          </label>
          <Input value={formData.address} onChange={e => handleChange("address", e.target.value)} placeholder="Street address, city, state, ZIP" className="h-14 rounded-md px-5" style={{
          backgroundColor: "var(--background)",
          borderColor: "var(--border)",
          color: "var(--foreground)"
        }} />
          {fieldErrors.address && <p className="text-destructive text-sm font-sans">{fieldErrors.address}</p>}
        </div>

        <div className="grid gap-2">
          <label className="font-sans text-xs font-semibold uppercase" style={{
          color: "var(--muted-foreground)",
          letterSpacing: "0.14em"
        }}>
            {"Service"}
          </label>
          <div className="flex flex-wrap gap-2">
            {availableServiceTypes.map((svc: any, i: number) => {
            const active = formData.service_type === svc.value;
            return <Button key={svc.value} data-index={i} type="button" onClick={() => handleChange("service_type", svc.value)} className="h-11 rounded-lg px-5 font-sans text-sm font-medium transition-colors" style={{
              backgroundColor: active ? "var(--secondary)" : "transparent",
              color: active ? "var(--secondary-foreground)" : "var(--foreground)",
              border: `1px solid ${active ? "var(--secondary)" : "var(--border)"}`
            }}>
                {svc.label}
              </Button>;
          })}
          </div>
          {fieldErrors.service_type && <p className="text-destructive text-sm font-sans">{fieldErrors.service_type}</p>}
        </div>

        <div className="grid gap-2">
          <label className="font-sans text-xs font-semibold uppercase" style={{
          color: "var(--muted-foreground)",
          letterSpacing: "0.14em"
        }}>
            {"Urgency"}
          </label>
          <RadioGroup value={formData.urgency} onValueChange={v => handleChange("urgency", v)} className="flex flex-wrap gap-6">
            {urgencyOptions.map((opt, i) => <label key={opt.value} data-index={i} htmlFor={`urgency-${opt.value}`} className="flex cursor-pointer items-center gap-3">
                <RadioGroupItem id={`urgency-${opt.value}`} value={opt.value} style={{
              borderColor: "var(--primary)"
            }} />
                <span className="font-sans text-sm font-medium" style={{
              color: "var(--foreground)"
            }}>
                  {opt.label}
                </span>
              </label>)}
          </RadioGroup>
        </div>

        <div className="grid gap-2">
          <label className="font-sans text-xs font-semibold uppercase" style={{
          color: "var(--muted-foreground)",
          letterSpacing: "0.14em"
        }}>
            {"Describe the Problem"}
          </label>
          <Textarea rows={4} value={formData.message} onChange={e => handleChange("message", e.target.value)} placeholder="Tell us what your AC, HVAC system, or appliance is doing..." className="min-h-32 rounded-md px-5 py-3" style={{
          backgroundColor: "var(--background)",
          borderColor: "var(--border)",
          color: "var(--foreground)"
        }} />
          {fieldErrors.message && <p className="text-destructive text-sm font-sans">{fieldErrors.message}</p>}
        </div>

        <div className="flex items-start gap-3 rounded-md p-4" style={{
        backgroundColor: "var(--background)",
        border: "1px solid var(--border)"
      }}>
          <Checkbox id="emergency-ack" checked={ackChecked} onCheckedChange={v => setAckChecked(Boolean(v))} className="mt-0.5" />
          <label htmlFor="emergency-ack" className="cursor-pointer font-sans text-sm" style={{
          color: "var(--foreground)"
        }}>
            {"I understand a local pro will contact me to confirm availability."}
          </label>
        </div>

        {submitError && <div className="rounded-md p-4" style={{
        backgroundColor: "color-mix(in srgb, var(--destructive) 10%, transparent)",
        border: "1.5px solid var(--destructive)"
      }}>
          <p className="font-sans text-sm" style={{
          color: "var(--destructive)"
        }}>
            {submitError}
          </p>
        </div>}

        <Button type="submit" disabled={isSubmitting} className="group h-16 w-full rounded-lg font-sans text-sm font-semibold uppercase" style={{
        backgroundColor: "var(--primary)",
        color: "var(--primary-foreground)",
        letterSpacing: "0.06em"
      }}>
          <span>{isSubmitting ? "Sending..." : "Submit Request"}</span>
          <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
        </Button>
      </form>
    </div>;
}
export default function Booking() {
  const reduce = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: reduce ? 0 : 0.09
      }
    }
  };
  const itemVariants = {
    hidden: reduce ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: reduce ? 0 : 0.6,
        ease: [0.2, 0.7, 0.2, 1] as [number, number, number, number]
      }
    }
  };
  return <section data-section-id="24663" id="bookingrequest" data-nav="light" className="w-full py-28 md:py-36 bg-background text-foreground">
      <div className="mx-auto w-full max-w-[1360px] px-5 md:px-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-10">
          <motion.div className="lg:col-span-5" variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }}>
            <motion.div variants={itemVariants}>
              <TickEyebrow label="Booking" />
            </motion.div>

            <motion.h2 variants={itemVariants} className="mt-8 font-mono font-bold uppercase" style={{
            color: "var(--foreground)",
            fontSize: "clamp(2.5rem, 5.6vw, 5rem)",
            lineHeight: 0.96,
            letterSpacing: "-0.005em"
          }}>
              {"Book Your Pro."}
            </motion.h2>

            <motion.p variants={itemVariants} className="mt-8 font-sans" style={{
            color: "color-mix(in srgb, var(--foreground) 90%, transparent)",
            fontSize: "clamp(1.125rem, 1.35vw, 1.3125rem)",
            lineHeight: 1.55
          }}>
              {"Connect with verified local contractors for fast, reliable home service solutions."}
            </motion.p>

            <motion.div variants={itemVariants} className="mt-12 space-y-6">
              <div className="flex items-start gap-4">
                <Phone className="mt-1 size-5 shrink-0" style={{
                color: "var(--foreground)"
              }} strokeWidth={1.75} />
                <div className="flex flex-col">
                  <span className="font-sans text-xs font-medium uppercase" style={{
                  color: "var(--muted-foreground)",
                  letterSpacing: "0.14em"
                }}>
                    {"Support"}
                  </span>
                  <a href="tel:+14708479076" className="mt-1 font-sans text-base font-medium underline underline-offset-4 decoration-2" style={{
                  color: "var(--foreground)",
                  textDecorationColor: "var(--primary)"
                }}>(470) 847-9076</a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Mail className="mt-1 size-5 shrink-0" style={{
                color: "var(--foreground)"
              }} strokeWidth={1.75} />
                <div className="flex flex-col">
                  <span className="font-sans text-xs font-medium uppercase" style={{
                  color: "var(--muted-foreground)",
                  letterSpacing: "0.14em"
                }}>
                    {"Email"}
                  </span>
                  <span className="mt-1 font-sans text-base font-medium" style={{
                  color: "var(--foreground)"
                }}>handyheroesinc@gmail.com</span>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <MapPin className="mt-1 size-5 shrink-0" style={{
                color: "var(--foreground)"
              }} strokeWidth={1.75} />
                <div className="flex flex-col">
                  <span className="font-sans text-xs font-medium uppercase" style={{
                  color: "var(--muted-foreground)",
                  letterSpacing: "0.14em"
                }}>
                    {"HQ"}
                  </span>
                  <span className="mt-1 font-sans text-base font-medium" style={{
                  color: "var(--foreground)"
                }}>
                    {"11175 Cicero Drive, Suite 100, Alpharetta, GA"}
                  </span>
                </div>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="mt-12 flex flex-wrap gap-3">
              <span className="inline-flex items-center rounded-md px-3 py-1.5 font-sans text-xs font-semibold uppercase" style={{
              color: "var(--foreground)",
              border: "1px solid var(--border)",
              backgroundColor: "var(--background)",
              letterSpacing: "0.14em"
            }}>
                {"Verified Pro"}
              </span>
              <span className="inline-flex items-center rounded-md px-3 py-1.5 font-sans text-xs font-semibold uppercase" style={{
              color: "var(--foreground)",
              border: "1px solid var(--border)",
              backgroundColor: "var(--background)",
              letterSpacing: "0.14em"
            }}>
                {"Local Pro"}
              </span>
            </motion.div>
          </motion.div>

          <motion.div className="lg:col-span-7" initial={reduce ? {
          opacity: 1
        } : {
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true,
          amount: 0.15
        }} transition={{
          duration: reduce ? 0 : 0.5,
          ease: "easeOut"
        }}>
            <ServiceRequestForm />
          </motion.div>
        </div>
      </div>
    </section>;
}